import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Play } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { accountApi } from '@/api/commercial/accounts';
import { CreditAnalysisStatus, creditAnalysisApi } from '@/api/commercial/credit-analysis';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FormSkeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { CREDIT_ANALYSIS_STATUS_LABEL } from '@/lib/constants/credit-analysis';
import { CreditAnalysisAnalysisTab } from './tabs/analysis-tab';
import { CreditAnalysisDocumentsTab } from './tabs/credit-analysis-history-tab';
import { CreditAnalysisGeneralTab } from './tabs/general-tab';
import { CreditAnalysisTimelineTab } from './tabs/timeline-tab';

type TabValue = 'account' | 'credit-analysis-history' | 'client-history' | 'analysis';

export function CreditAnalysisUpdatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabValue>('account');
  const [showStartAnalysisDialog, setShowStartAnalysisDialog] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const startAnalysisMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Credit analysis ID is required');
      return creditAnalysisApi.startAnalysis(id);
    },
    onSuccess: () => {
      toast({
        title: 'Análise iniciada',
        description: 'A análise de crédito foi iniciada com sucesso.',
      });
      queryClient.invalidateQueries({ queryKey: ['credit-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['credit-analysis', id] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const { data: creditAnalysis, isLoading } = useQuery({
    queryKey: ['credit-analysis', id],
    queryFn: () => creditAnalysisApi.findOne(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
  });

  const { data: account } = useQuery({
    queryKey: ['accounts', creditAnalysis?.accountId],
    queryFn: () => accountApi.findOne(creditAnalysis?.accountId!),
    enabled: !!creditAnalysis?.accountId,
    staleTime: 60 * 1000,
  });

  const statusInfo = creditAnalysis?.status
    ? CREDIT_ANALYSIS_STATUS_LABEL[creditAnalysis.status]
    : null;

  if (isLoading) {
    return (
      <div className="w-full">
        <Card>
          <CardContent className="pt-6">
            <FormSkeleton />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!creditAnalysis) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Análise de crédito não encontrada</p>
        <Button onClick={() => navigate('/commercial/credit-analysis')} className="mt-4">
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Voltar"
            onClick={() => navigate('/commercial/credit-analysis')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flexm flex-col">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Análise de Crédito -{' '}
                {creditAnalysis?.clientName || account?.name || account?.fantasy_name || 'Cliente'}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              Status:
              {statusInfo && (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.color}`}
                >
                  {statusInfo.label}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Start Analysis Button - appears when PENDING_ANALYSIS and Analysis tab is active */}
        {creditAnalysis?.status === CreditAnalysisStatus.PENDING_ANALYSIS &&
          activeTab === 'analysis' && (
            <Button
              onClick={() => setShowStartAnalysisDialog(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Play className="h-4 w-4 mr-2" />
              Iniciar Análise
            </Button>
          )}
      </div>

      {/* Confirmation Dialog for Starting Analysis */}
      <AlertDialog open={showStartAnalysisDialog} onOpenChange={setShowStartAnalysisDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Início da Análise de Crédito?</AlertDialogTitle>
            <AlertDialogDescription>
              Ao confirmar, você assumirá a responsabilidade por esta análise e o status será
              alterado para <strong>Em Análise</strong>. Deseja prosseguir?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline" disabled={startAnalysisMutation.isPending}>
                Voltar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                onClick={() => {
                  startAnalysisMutation.mutate();
                  setShowStartAnalysisDialog(false);
                }}
                disabled={startAnalysisMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {startAnalysisMutation.isPending ? 'Iniciando...' : 'Confirmar e Iniciar'}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)}>
        <TabsList>
          <TabsTrigger value="account">Dados da conta</TabsTrigger>
          <TabsTrigger value="credit-analysis-history">Histórico de análises</TabsTrigger>
          <TabsTrigger value="client-history">Histórico do cliente</TabsTrigger>
          <TabsTrigger value="analysis">Análise</TabsTrigger>
        </TabsList>

        <TabsContent value="account">
          <CreditAnalysisGeneralTab creditAnalysis={creditAnalysis} />
        </TabsContent>

        <TabsContent value="credit-analysis-history">
          <CreditAnalysisDocumentsTab creditAnalysis={creditAnalysis} />
        </TabsContent>

        <TabsContent value="client-history">
          <CreditAnalysisTimelineTab creditAnalysis={creditAnalysis} />
        </TabsContent>

        <TabsContent value="analysis">
          <CreditAnalysisAnalysisTab creditAnalysis={creditAnalysis} />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
